import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSearchParams } from 'react-router-dom';
import { useSWRConfig } from 'swr';

import MainTable from '@components/MainTable';
import { Status } from '@api/notifications/model/enums';
import { useMediaQuery, useTheme } from '@mui/material';
import { documentColumnVisibility } from './columnVisibility';
import MainTablePagination from '@components/MainTable/MainTablePagination';
import { useTanStackTable } from '@hooks/useTanStackTable';
import { UserDocument } from '@api/document/model/types';
import { DEFAULT_PAGE_SIZE } from '@shared/constants/common';
import { usePreserveQueryParamsNavigate } from '@hooks/usePreserveQueryParamsNavigate';
import { usePageContext } from '@providers/PageMetaProvider/model/PageContext';
import useDataFetcher from '@hooks/useDataFetcher/useDataFetcher';
import { urls } from '@api/urls.ts';
import { AllDocumentsTableMeta } from '@app/types/overrides/table-meta';
import useToast from '@hooks/useToast';
import useConfirmationModal from '@hooks/useConfirmationModal';
import { IconCancel } from '@tabler/icons-react';
import {
  BackendError,
  extractBackendError,
} from '@utils/handle-backend-error.ts';
import createDocumentColumns from '@features/documents/containers/DocumentsTable/columns';
import { buildDepositionSummaryActionsMap } from '@features/documents/utils';
import { cancelDocumentInstance } from '@api/document-instance';
import { DocumentType } from '@utils/enums.ts';
import { downloadUserDocument } from './download';
import {
  cancelConfirmationText,
  cancelledRowStatus,
  refetchAfterStatus,
} from './cancelled';
import {
  useWebSocket,
  WebsocketTypeEnum,
} from '@providers/WebsocketProvider/ui/WebsocketProvider';
import { cancelDepositionSummary } from '@api/deposition-summary';
import { cancelUseCase } from '@app/services/api/use-case';
import { deleteCase } from '@api/case/apiService';
import {
  getMyDocumentsCacheKeys,
  updateMyDocumentsCache,
} from '@shared/utils/my-documents-cache';
import {
  clearDocumentProgress,
  resetDocumentProgress,
} from '@shared/hooks/useGenerationProgress';

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

interface DocumentsTableProps {
  documentType: DocumentType | 'all' | 'cases';
}

function DocumentsTable({ documentType = 'all' }: DocumentsTableProps) {
  const {
    showAlertConfirmation,
    showDeleteConfirmation,
    showConfirmationModal,
    closeModal,
  } = useConfirmationModal();
  const { showSuccess, showError } = useToast();
  const { setValues, values } = usePageContext();
  const navigateWithParams = usePreserveQueryParamsNavigate();
  const [searchParams] = useSearchParams();
  const currentPage = searchParams.get('page') ?? '1';
  const wsMessage = useWebSocket();
  const { mutate: globalMutate, cache } = useSWRConfig();
  const [removedKeys, setRemovedKeys] = useState<Set<string>>(new Set());
  const tableDataRef = useRef<UserDocument[]>([]);
  const [cancelledOverrides, setCancelledOverrides] = useState<Set<string>>(
    new Set()
  );

  const getDocumentKey = useCallback(
    (doc: Pick<UserDocument, 'id' | 'type'>) => `${doc.type}-${doc.id}`,
    []
  );

  const applyCancelledOverrides = useCallback(
    (docs: UserDocument[]) => {
      if (!cancelledOverrides.size) return docs;
      return docs.map((doc) => {
        if (!cancelledOverrides.has(getDocumentKey(doc))) return doc;
        // ZLAYT-1603: only a stale "still running" row is held on Draft; a
        // cancelled rerun shows the finished version's status again.
        const status = cancelledRowStatus(doc.generation_status || doc.status);
        return {
          ...doc,
          generation_status: status,
          status,
        };
      });
    },
    [cancelledOverrides, getDocumentKey]
  );

  const groupIdValue = searchParams.get('use_case_group_id');
  const clientFilterId = (values as any)?.clientFilterId;
  const clientFilterName = (values as any)?.clientFilterName;
  const documentTypeFilter = (values as any)?.documentTypeFilter ?? 'all';
  const statusFilter = (values as any)?.statusFilter ?? 'all';
  // The My Documents search box writes ?search= (ZLAYT-1423).
  const searchValue = searchParams.get('search') ?? '';

  const params = useMemo(() => {
    const p = new URLSearchParams();

    // Pagination
    p.append('page', currentPage);
    p.append('page_size', String(DEFAULT_PAGE_SIZE));

    // Document type filter
    let docType = documentType;
    if (documentTypeFilter !== 'all') {
      // Map frontend filter values to backend values
      const map: Record<string, string> = {
        demand: DocumentType.DEMAND_LETTER,
        deposition: DocumentType.DEPOSITION_SUMMARY,
        usecase: DocumentType.USE_CASE,
      };
      if (map[documentTypeFilter]) {
        docType = map[documentTypeFilter] as any;
      }
    }

    if (docType !== 'all' && docType !== 'cases') {
      p.append('document_type', docType);
    } else if (docType === 'cases') {
      p.append('document_type', 'cases');
    }
    // For 'all', don't send document_type (backend defaults to all)

    // Use case group filter
    if (groupIdValue) {
      p.append('use_case_group_id', groupIdValue);
    }

    // Client filter
    if (clientFilterId && clientFilterId !== 'all') {
      p.append('client_id', clientFilterId);
    }
    if (clientFilterName && clientFilterName.trim()) {
      p.append('client_name', clientFilterName.trim());
    }

    // Status filter
    if (statusFilter && statusFilter !== 'all') {
      p.append('status', statusFilter);
    }

    // Search filter
    if (searchValue && searchValue.trim()) {
      p.append('search', searchValue.trim());
    }

    return p;
  }, [
    currentPage,
    documentType,
    groupIdValue,
    clientFilterId,
    clientFilterName,
    documentTypeFilter,
    statusFilter,
    searchValue,
  ]);

  const { data, isLoading, mutate } = useDataFetcher<
    PaginatedResponse<UserDocument>
  >({
    url: urls.myDocuments,
    params,
  });

  // Extract results and count from paginated response
  // ✅ Memoize to prevent new array reference on every render
  const documents = useMemo(() => data?.results ?? [], [data?.results]);
  const getRowKey = useCallback(
    (doc: UserDocument) => `${doc.type}-${doc.id}`,
    []
  );
  const filteredDocuments = useMemo(
    () => documents.filter((doc) => !removedKeys.has(getRowKey(doc))),
    [documents, removedKeys, getRowKey]
  );
  const totalCount = data?.count ?? 0;

  // Polling fallback for documents with pending/in-progress status
  // Backend status values from models:
  // - DocumentInstanceStatus (Demand Letters): "Temporary" (excluded), "Draft", "Pending", "Completed", "Failed"
  // - DepositionSummaryVersion: "Temporary" (defaults to Draft), "Draft", "Pending", "Completed", "Failed"
  // - UseCaseVersion: "Temporary" (defaults to Draft), "Draft", "Pending", "Completed", "Failed"
  // Note: "Pending" is the main status for active generation. "Temporary" is excluded from API responses.
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastPollTimeRef = useRef<number>(0);
  const hasPendingRef = useRef<boolean>(false);

  // Check if there are any documents with pending/in-progress status
  // "Pending" is the status that indicates active generation
  const hasPendingDocuments = useMemo(() => {
    if (!filteredDocuments || filteredDocuments.length === 0) return false;
    return filteredDocuments.some((doc) => {
      if (!doc.generation_status) return false;
      const status = doc.generation_status.trim();
      // Check for "Pending" status (case-insensitive to handle any variations)
      return status.toLowerCase() === 'pending';
    });
  }, [filteredDocuments]);

  useEffect(() => {
    // Don't poll if data is still loading or if we have no documents
    if (isLoading || !data) {
      // Clear any existing polling while loading
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    // Only update polling if the pending status actually changed
    if (hasPendingDocuments === hasPendingRef.current) {
      return; // No change, skip effect
    }

    hasPendingRef.current = hasPendingDocuments;

    // Clear existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Only start polling if there are actually pending documents
    if (hasPendingDocuments) {
      // Poll every 5 seconds for status updates (fallback if WebSocket fails)
      const POLL_INTERVAL = 5000;
      const MIN_POLL_INTERVAL = 3000; // Minimum 3 seconds between polls

      pollingIntervalRef.current = setInterval(() => {
        const now = Date.now();
        // Throttle polling to avoid too frequent requests
        if (now - lastPollTimeRef.current >= MIN_POLL_INTERVAL) {
          lastPollTimeRef.current = now;
          mutate();
        }
      }, POLL_INTERVAL);
    }

    // Cleanup on unmount or when no pending documents
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [hasPendingDocuments, mutate, isLoading, data]);

  const actionsMap = useMemo(() => {
    const baseActions = buildDepositionSummaryActionsMap(navigateWithParams);

    return {
      ...baseActions,
      Case: {
        onEdit: (id: number) => navigateWithParams(`/cases/edit/${id}`),
        onDelete: async (id: number) => {
          return new Promise((resolve, reject) =>
            deleteCase(
              id,
              () => resolve(undefined),
              (error) => reject(error) // Let the main error handler deal with it
            )
          );
        },
        onView: (id: number) => navigateWithParams(`/cases/edit/${id}`),
      },
    };
  }, [navigateWithParams]);

  const { data: paymentHistoryData } = useDataFetcher<any>({
    url: urls.caseSubscription.invoices,
    revalidateOnFocus: false,
    dedupingInterval: 60000, // 1 minute
  });

  const { data: companyMembersData } = useDataFetcher<any[]>({
    url: urls.companyMembers.base,
    revalidateOnFocus: false,
    dedupingInterval: 60000,
  });

  const columns = useMemo(
    () =>
      createDocumentColumns(mutate, navigateWithParams, documentType, {
        paymentHistory: paymentHistoryData?.invoices_history,
        companyMembers: companyMembersData,
        searchTerm: searchValue,
      }),
    [
      mutate,
      navigateWithParams,
      documentType,
      paymentHistoryData,
      companyMembersData,
      searchValue,
    ]
  );

  const updateDocumentsCache = useCallback(
    (
      updater: (
        current?: PaginatedResponse<UserDocument>
      ) => PaginatedResponse<UserDocument> | undefined
    ) => {
      getMyDocumentsCacheKeys(cache).forEach((key) => {
        globalMutate(key, updater, { revalidate: false });
      });
    },
    [cache, globalMutate]
  );

  const handleDelete = useCallback(
    async (selectedDocument: UserDocument) => {
      try {
        await actionsMap[selectedDocument.type].onDelete(selectedDocument.id);
        const rowKey = getRowKey(selectedDocument);
        setRemovedKeys((prev) => {
          const next = new Set(prev);
          next.add(rowKey);
          return next;
        });
        updateDocumentsCache((current) => {
          if (!current) return current;
          const nextResults = current.results.filter(
            (doc) => getRowKey(doc) !== rowKey
          );
          const removedCount = current.results.length - nextResults.length;
          return {
            ...current,
            results: nextResults,
            count: Math.max(0, current.count - removedCount),
          };
        });
        setTimeout(() => {
          getMyDocumentsCacheKeys(cache).forEach((key) => {
            globalMutate(key, undefined, { revalidate: true });
          });
        }, 1500);
        showSuccess(
          'Document deleted',
          (selectedDocument.type as any) === 'Case'
            ? 'Case deleted successfully'
            : `Document ${selectedDocument.name} has been deleted successfully.`
        );
      } catch (error: any) {
        console.error(
          `Error while deleting document with id: ${selectedDocument.id}`,
          error
        );

        // Check if it's a case with associated documents error
        if (error?.response?.data?.error?.code === 'case_has_documents') {
          const errorData = error.response.data.error;
          const count = errorData.document_count;
          const docNames = errorData.document_names || [];
          const docIds = errorData.document_ids || [];
          console.log('[DEBUG] Cannot delete case - Associated documents:', {
            count,
            document_names: docNames,
            document_ids: docIds,
            full_error: errorData,
          });

          // Show document IDs to help user find them
          const idList =
            docIds.length > 0 ? ` (IDs: ${docIds.join(', ')})` : '';
          showError(
            'Cannot Delete Case',
            `Cannot delete this case because it has ${count} associated demand letter(s)${idList}. Please go to My Documents and delete these demand letters first.`
          );
        } else if (
          error?.response?.data?.error &&
          error?.response?.data?.document_count
        ) {
          const count = error.response.data.document_count;
          showError(
            'Cannot Delete Case',
            `Cannot delete this case because it has ${count} document(s) using it. Please delete those documents first.`
          );
        } else if (error?.message) {
          // Use custom error message if available (e.g., from our case deletion handler)
          showError('Error', error.message);
        } else {
          showError('Error', 'There was an error deleting the document');
        }
      } finally {
        closeModal();
      }
    },
    [
      actionsMap,
      cache,
      closeModal,
      getRowKey,
      globalMutate,
      showError,
      showSuccess,
      updateDocumentsCache,
    ]
  );

  const handleDownloadDocument = useCallback(
    async (selectedDocument: UserDocument) => {
      if (selectedDocument.type === DocumentType.DEMAND_LETTER) {
        showAlertConfirmation(
          'Hey, check this out!',
          'Before you download your letter, take a quick moment to double-check all the details.',
          () => {
            downloadUserDocument(selectedDocument);
            closeModal();
          }
        );
        return;
      }

      try {
        showAlertConfirmation(
          'Hey, check this out!',
          'Before you download your deposition summary, take a quick moment to double-check all the details.',
          () => {
            downloadUserDocument(selectedDocument);
            closeModal();
          }
        );
      } catch (error) {
        showError('Error', 'There was an error while downloading the document');
      }
    },
    [showAlertConfirmation, closeModal, showError]
  );

  const handleCancelDocument = useCallback(
    async (selectedDocument: UserDocument) => {
      try {
        // Call appropriate cancel API based on document type
        if (selectedDocument.type === DocumentType.USE_CASE) {
          await cancelUseCase(selectedDocument.id);
        } else if (selectedDocument.type === DocumentType.DEPOSITION_SUMMARY) {
          await cancelDepositionSummary(selectedDocument.id);
        } else {
          await cancelDocumentInstance(selectedDocument.id);
        }

        const cancelledId = String(selectedDocument.id);
        const cancelledKey = getDocumentKey(selectedDocument);
        setCancelledOverrides((prev) => {
          const next = new Set(prev);
          next.add(cancelledKey);
          return next;
        });
        const updated = tableDataRef.current.map((doc) => {
          if (String(doc.id) !== cancelledId) return doc;
          if (doc.type !== selectedDocument.type) return doc;
          return {
            ...doc,
            generation_status: 'Draft',
            status: 'Draft',
          };
        });
        tableDataRef.current = updated;
        setData(updated);
        const pipeline =
          selectedDocument.type === DocumentType.DEPOSITION_SUMMARY
            ? 'deposition_summary'
            : selectedDocument.type === DocumentType.USE_CASE
              ? 'use_case'
              : 'demand_letter';
        clearDocumentProgress(cancelledId, pipeline);

        updateMyDocumentsCache(cache, globalMutate, (current: any) => {
          if (!current?.results) return current;
          return {
            ...current,
            results: current.results.map((doc: any) => {
              if (
                String(doc.id) !== cancelledId ||
                doc.type !== selectedDocument.type
              ) {
                return doc;
              }
              return {
                ...doc,
                generation_status: 'Draft',
                status: 'Draft',
              };
            }),
          };
        });

        await mutate();
        showSuccess(
          'Document canceled',
          `Document ${selectedDocument.name} has been canceled successfully.`
        );
      } catch (error) {
        const errorMessage = extractBackendError(error as BackendError);
        showError('Error', errorMessage);
      } finally {
        closeModal();
      }
    },
    [
      mutate,
      showSuccess,
      showError,
      closeModal,
      getDocumentKey,
      cache,
      globalMutate,
    ]
  );

  useEffect(() => {
    if (setValues) {
      setValues((prev) => ({
        ...prev,
        generateModalOpened: prev.generateModalOpened,
        generateDepositionSummaryModalOpened:
          prev.generateDepositionSummaryModalOpened,
      }));
    }
  }, [setValues]);

  const handleEditRow = useCallback(
    (document: UserDocument) => {
      actionsMap[document.type].onEdit(document.id);
    },
    [actionsMap]
  );

  const handleDownloadRow = useCallback(
    async (document: UserDocument) => {
      await handleDownloadDocument(document);
    },
    [handleDownloadDocument]
  );

  const handleDeleteRow = useCallback(
    (document: UserDocument) => {
      showDeleteConfirmation(
        () => handleDelete(document),
        `Are you sure you want to delete document ${document.name}? This action cannot be undone.`
      );
    },
    [showDeleteConfirmation, handleDelete]
  );

  const handleViewRow = useCallback(
    (document: UserDocument) => {
      actionsMap[document.type].onView(document.id);
    },
    [actionsMap]
  );

  // ZLAYT-1579: a click anywhere on a row opens it, for the same documents the
  // eye icon opens (completed ones, and drafts or failures, whose page offers
  // Edit and Delete). A row still generating does nothing. The row may be a
  // render behind a socket status change, so read the latest status.
  const handleRowClick = useCallback(
    (document: UserDocument) => {
      const latest =
        tableDataRef.current.find(
          (doc) => doc.id === document.id && doc.type === document.type
        ) || document;
      const openable = [Status.Completed, Status.Draft, Status.Failed];
      if (!openable.includes(latest.generation_status as Status)) return;
      handleViewRow(latest);
    },
    [handleViewRow]
  );

  const handleCancelRow = useCallback(
    (document: UserDocument) => {
      showConfirmationModal({
        icon: <IconCancel />,
        title: 'Cancel generation',
        color: 'error',
        cancelLabel: 'No, Keep Generating',
        confirmLabel: 'Yes, Cancel',
        content: cancelConfirmationText(document),
        onConfirm: () => handleCancelDocument(document),
      });
    },
    [showConfirmationModal, handleCancelDocument]
  );

  const normalizeStatusLabel = useCallback((value?: string) => {
    const normalized = String(value || '').toLowerCase();
    if (normalized.includes('cancel')) {
      return 'Draft';
    }
    if (normalized.includes('fail')) {
      return 'Failed';
    }
    if (normalized.includes('complete')) return 'Completed';
    if (normalized.includes('pending')) return 'Pending';
    return value || '';
  }, []);

  const { table, setData } = useTanStackTable<UserDocument>({
    tableData: filteredDocuments,
    columnConfig: columns,
    // The columns carry the search term for the snippet highlight
    // (ZLAYT-1439), so the table must follow them as the user types.
    syncColumns: true,
    options: {
      getRowId: (r) => `${r.type}-${r.id}`,
      manualPagination: true,
      pageCount: Math.ceil(totalCount / DEFAULT_PAGE_SIZE),
      autoResetPageIndex: false,
      initialState: {
        pagination: {
          pageIndex: currentPage ? Number(currentPage) - 1 : 0,
          pageSize: DEFAULT_PAGE_SIZE,
        },
      },
      meta: {
        handleEditRow,
        handleDownloadRow,
        handleDeleteRow,
        handleViewRow,
        handleCancelRow,
      } as AllDocumentsTableMeta<UserDocument>,
    },
  });

  // ZLAYT-1573: keep Client, Type, Status and Action in view on a phone.
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  useEffect(() => {
    table.setColumnVisibility((prev) => ({
      ...prev,
      ...documentColumnVisibility(isPhone),
    }));
  }, [isPhone, table]);

  useEffect(() => {
    if (!wsMessage?.type || !wsMessage?.payload) return;

    const { type, payload } = wsMessage;
    if (!payload?.document_id || !payload?.status) return;

    let expectedType: DocumentType | null = null;
    if (type === WebsocketTypeEnum.DOCUMENT_INSTANCE_STATUS) {
      expectedType = DocumentType.DEMAND_LETTER;
    } else if (type === WebsocketTypeEnum.DEPOSITION_SUMMARY_VERSION_STATUS) {
      expectedType = DocumentType.DEPOSITION_SUMMARY;
    } else if (type === WebsocketTypeEnum.USE_CASE_VERSION_STATUS) {
      expectedType = DocumentType.USE_CASE;
    }

    if (!expectedType) return;

    const normalizedStatus = normalizeStatusLabel(payload.status);
    const targetId = String(payload.document_id);
    const prevDoc = tableDataRef.current.find(
      (doc) => String(doc.id) === targetId && doc.type === expectedType
    );
    const prevStatus = normalizeStatusLabel(prevDoc?.generation_status || '');
    const isPendingStatus = (value: string) =>
      value === 'Pending' || value === 'In Progress';
    if (
      isPendingStatus(normalizedStatus) &&
      prevStatus &&
      !isPendingStatus(prevStatus)
    ) {
      const pipeline =
        expectedType === DocumentType.DEPOSITION_SUMMARY
          ? 'deposition_summary'
          : expectedType === DocumentType.USE_CASE
            ? 'use_case'
            : 'demand_letter';
      resetDocumentProgress(targetId, pipeline);
    }
    if (normalizedStatus !== 'Pending' && normalizedStatus !== 'In Progress') {
      const key = `${expectedType}-${targetId}`;
      setCancelledOverrides((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
    const updated = tableDataRef.current.map((doc) => {
      if (String(doc.id) !== targetId) return doc;
      if (doc.type !== expectedType) return doc;
      return {
        ...doc,
        generation_status: normalizedStatus,
        status: normalizedStatus,
      };
    });

    tableDataRef.current = updated;
    setData(updated);

    // The socket carries the new status but not the reason behind it, so a row
    // that fails while the list is open would show "Failed" with nothing to
    // click. Refetch that once so the row gets its failure_reason.
    // ZLAYT-1603: after a cancel, refetch too, so a rerun of a finished
    // document shows that finished version's status again.
    if (refetchAfterStatus(payload.status, prevStatus)) {
      mutate();
    }
  }, [mutate, normalizeStatusLabel, setData, wsMessage]);

  // Update table data when documents change
  useEffect(() => {
    const updated = applyCancelledOverrides(filteredDocuments);
    tableDataRef.current = updated;
    setData(updated);
  }, [applyCancelledOverrides, filteredDocuments, setData]);

  return (
    <>
      <MainTable
        table={table}
        isLoading={isLoading}
        onRowClick={handleRowClick}
      />
      <MainTablePagination table={table} isLoading={isLoading} />
    </>
  );
}

export default DocumentsTable;
